// Canonical "is this invoice paid?" predicate — the single source of truth the
// approval layer and the payment (mBank outbound) layer BOTH consult, so they can
// never disagree about which invoices are still owed.
//
// Dependency-free (no DAOs, no Supabase) so it imports cleanly into the Deno edge
// functions (mbank-sync / mbank-send-payment), the shared services, and the React
// frontend alike.
//
// Why both fields? `invoices.payment_status` is OWNED by the bank pipeline
// (recompute_invoice_payment_status trigger on mbank_payment_links). Fakturownia
// marks an invoice paid the moment it's recorded there — long before, or entirely
// independently of, any bank-side reconciliation. An invoice can therefore be
// FA-paid while its local payment_status still reads 'unpaid'/'partial' (the bank
// pipeline never ran). Paying it again would double-pay the supplier.
//
// This mirrors InvoiceApprovalService.isPaid() — that method now delegates here so
// the two layers share one definition.

/** The minimal shape the paid check needs. Both InvoiceRow and the edge-function
 *  row projections satisfy it. */
export interface InvoicePaidFields {
  payment_status?: string | null;
  fakturownia_payment_status?: string | null;
}

/**
 * True when the invoice is paid per EITHER the bank-driven local status OR
 * Fakturownia. Payment gates (mBank prepare / send / isMbankPayable) must
 * exclude anything this returns true for — regardless of what the local
 * payment_status alone says.
 */
export function isInvoicePaid(inv: InvoicePaidFields): boolean {
  return inv.payment_status === 'paid' || inv.fakturownia_payment_status === 'paid';
}

/**
 * True when Fakturownia reports the invoice paid but the bank pipeline holds no
 * evidence at all (`payment_status` unpaid/absent). This is the display-side
 * companion to {@link isInvoicePaid}: the gates already refuse to pay such an
 * invoice, so the view must say WHY instead of "Nieopłacona". Deliberately false
 * for 'partial' (bank evidence exists — the progress display wins) and 'on_hold'
 * (the hold pill carries an operator decision in flight).
 */
export function isFaOnlyPaid(inv: InvoicePaidFields): boolean {
  return (
    inv.fakturownia_payment_status === 'paid' &&
    (inv.payment_status == null || inv.payment_status === 'unpaid')
  );
}
